import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const MOBILE_GALLERY_QUERY = '(max-width: 768px)';
const SWIPE_DETECT_THRESHOLD = 10;
const SWIPE_COMMIT_THRESHOLD = 40;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupMobileGridSwipe();
  }

  #controller = new AbortController();

  /** @type {number} */
  #mobileSwipeStartIndex = 0;

  /** @type {boolean} */
  #mobileTouchActive = false;

  /** @type {boolean} */
  #mobileDidSwipe = false;

  /** @type {boolean} */
  #mobileIsHorizontalSwipe = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Keeps mobile grid swipes to one slide and prevents zoom opening during swipes.
   */
  #setupMobileGridSwipe() {
    if (!window.matchMedia(MOBILE_GALLERY_QUERY).matches) return;
    if (this.presentation !== 'grid') return;

    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;

    const progressFill = this.querySelector('.media-gallery__scroll-progress-fill');
    const { signal } = this.#controller;
    let touchStartX = 0;
    let touchStartY = 0;

    const getVisibleSlides = () => [...grid.children].filter((slide) => slide.offsetParent !== null);

    const getSlideIndex = () => {
      const slides = getVisibleSlides();
      if (!slides.length) return 0;

      const scrollLeft = grid.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const slides = getVisibleSlides();
      if (!slides.length) return;

      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      const targetSlide = slides[targetIndex];
      if (!targetSlide) return;

      grid.scrollTo({ left: targetSlide.offsetLeft, behavior });
    };

    const getClampedScrollLeft = (startIndex, deltaX) => {
      const slides = getVisibleSlides();
      const startSlide = slides[startIndex];
      if (!startSlide) return grid.scrollLeft;

      const startLeft = startSlide.offsetLeft;
      const minLeft = startIndex > 0 ? slides[startIndex - 1].offsetLeft : startLeft;
      const maxLeft = startIndex < slides.length - 1 ? slides[startIndex + 1].offsetLeft : startLeft;
      const nextLeft = startLeft + deltaX;

      return Math.max(minLeft, Math.min(maxLeft, nextLeft));
    };

    const resolveTargetIndex = (startIndex, deltaX) => {
      const maxIndex = getVisibleSlides().length - 1;

      if (deltaX > SWIPE_COMMIT_THRESHOLD) {
        return Math.min(maxIndex, startIndex + 1);
      }

      if (deltaX < -SWIPE_COMMIT_THRESHOLD) {
        return Math.max(0, startIndex - 1);
      }

      return startIndex;
    };

    const updateScrollProgress = (isDragging = false) => {
      if (!progressFill) return;

      const slides = getVisibleSlides();
      const progress = progressFill.parentElement;

      if (!slides.length || slides.length <= 1) {
        if (progress) progress.hidden = true;
        return;
      }

      if (progress) progress.hidden = false;

      const maxScroll = grid.scrollWidth - grid.clientWidth;
      const percent = maxScroll <= 0 ? 100 : (grid.scrollLeft / maxScroll) * 100;

      progressFill.style.width = `${Math.min(100, Math.max(0, percent))}%`;
      progressFill.classList.toggle('is-dragging', isDragging);
    };

    const blockZoomClick = (event) => {
      if (!this.#mobileDidSwipe) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#mobileDidSwipe = false;
    };

    grid.addEventListener(
      'touchstart',
      (event) => {
        this.#mobileTouchActive = true;
        this.#mobileDidSwipe = false;
        this.#mobileIsHorizontalSwipe = false;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        this.#mobileSwipeStartIndex = getSlideIndex();
      },
      { passive: true, signal }
    );

    grid.addEventListener(
      'touchmove',
      (event) => {
        const touch = event.touches[0];
        const moveX = Math.abs(touch.clientX - touchStartX);
        const moveY = Math.abs(touch.clientY - touchStartY);

        if (!this.#mobileIsHorizontalSwipe && moveX > SWIPE_DETECT_THRESHOLD) {
          this.#mobileIsHorizontalSwipe = moveX > moveY;
        }

        if (!this.#mobileIsHorizontalSwipe) return;

        event.preventDefault();
        this.#mobileDidSwipe = true;

        const deltaX = touchStartX - touch.clientX;
        grid.scrollLeft = getClampedScrollLeft(this.#mobileSwipeStartIndex, deltaX);
        updateScrollProgress(true);
      },
      { passive: false, signal }
    );

    grid.addEventListener(
      'touchend',
      (event) => {
        this.#mobileTouchActive = false;

        if (!this.#mobileIsHorizontalSwipe) return;

        const deltaX = touchStartX - event.changedTouches[0].clientX;
        const targetIndex = resolveTargetIndex(this.#mobileSwipeStartIndex, deltaX);

        scrollToIndex(targetIndex);
        this.#mobileIsHorizontalSwipe = false;
        updateScrollProgress(false);
      },
      { passive: true, signal }
    );

    grid.addEventListener(
      'touchcancel',
      () => {
        this.#mobileTouchActive = false;

        if (!this.#mobileIsHorizontalSwipe) return;

        scrollToIndex(this.#mobileSwipeStartIndex);
        this.#mobileIsHorizontalSwipe = false;
        updateScrollProgress(false);
      },
      { passive: true, signal }
    );

    grid.addEventListener(
      'scroll',
      () => {
        updateScrollProgress(this.#mobileTouchActive && this.#mobileIsHorizontalSwipe);
      },
      { passive: true, signal }
    );

    updateScrollProgress(false);

    grid.querySelectorAll('.product-media-container__zoom-button').forEach((button) => {
      button.addEventListener('click', blockZoomClick, { capture: true, signal });
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
